import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Truck, CheckCircle2, LogOut, Package, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, peso, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const STATUS_COLORS = {
  assigned: "bg-[#8E44AD] text-white",
  out_for_delivery: "bg-[#E67E22] text-white",
  delivered: "bg-[#2F5233] text-white",
};

export default function DriverPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyOrder, setVerifyOrder] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try { await api.post(`/orders/${id}/status`, { status }); toast.success(`Marked as ${status.replace(/_/g, " ")}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openDeliverDialog = (o) => { setVerifyOrder(o); setCode(""); };
  const closeDeliverDialog = () => { setVerifyOrder(null); setCode(""); };

  const verifyAndDeliver = async () => {
    if (!verifyOrder) return;
    const cleaned = code.replace(/\D/g, "");
    if (cleaned.length !== 4) { toast.error("Enter the 4-digit code from the customer"); return; }
    setVerifying(true);
    try {
      await api.post(`/orders/${verifyOrder.id}/verify-delivery-code`, { code: cleaned });
      await api.post(`/orders/${verifyOrder.id}/status`, { status: "delivered" });
      toast.success("Delivery confirmed");
      closeDeliverDialog();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const active = orders.filter((o) => o.status !== "delivered");
  const done = orders.filter((o) => o.status === "delivered");

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/assets/arbe-logo.png" alt="Arbe" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-heading font-extrabold text-[#1C2826]">Driver Portal</div>
              <div className="text-xs text-[#738380]">{user?.name}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="driver-logout">
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Today's deliveries</div>
              <h1 className="font-heading text-3xl font-extrabold text-[#1C2826] tracking-tight">Active orders</h1>
            </div>
            <div className="text-xs px-2 py-1 bg-[#2F5233] text-white rounded-full font-bold" data-testid="driver-count">{active.length}</div>
          </div>
          {loading ? (
            <div className="py-20 flex justify-center"><div className="h-10 w-10 border-4 border-[#2F5233] border-t-transparent rounded-full animate-spin" /></div>
          ) : active.length === 0 ? (
            <div className="mt-6 bg-white border border-[#E1E5DF] rounded-2xl p-10 text-center">
              <Package className="h-12 w-12 text-[#2F5233] mx-auto" />
              <div className="mt-3 font-heading font-semibold">No active deliveries</div>
              <div className="text-sm text-[#738380]">You're all caught up! Check back later.</div>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {active.map((o) => (
                <div key={o.id} className="bg-white border border-[#E1E5DF] rounded-2xl p-4" data-testid={`driver-order-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-[#738380]">Order</div>
                      <div className="font-heading font-bold text-[#1C2826]">{o.number}</div>
                    </div>
                    <Badge className={STATUS_COLORS[o.status] || "bg-gray-200"}>{o.status.replace(/_/g, " ")}</Badge>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="font-semibold text-[#1C2826]">{o.customer_name}</div>
                    <a href={`tel:${o.customer_phone}`} className="flex items-center gap-2 text-[#2F5233] hover:underline">
                      <Phone className="h-4 w-4" /> {o.customer_phone}
                    </a>
                    <div className="flex items-start gap-2 text-[#4A5D5A]">
                      <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" /> {o.customer_address}
                    </div>
                    {o.notes && <div className="text-xs bg-[#F9F9F7] border border-[#E1E5DF] rounded p-2">Note: {o.notes}</div>}
                  </div>
                  <div className="mt-3 text-sm">
                    <span className="text-[#738380]">{o.items.length} item(s) · </span>
                    <span className="font-heading font-bold text-[#2F5233]">{peso(o.total)}</span>
                    <span className="text-[#738380]"> · {o.payment_method.toUpperCase()}</span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <Button
                      disabled={o.status === "out_for_delivery"}
                      onClick={() => update(o.id, "out_for_delivery")}
                      className="h-12 bg-[#E67E22] hover:bg-[#D35400] text-white rounded-lg font-semibold"
                      data-testid={`out-${o.id}`}
                    >
                      <Truck className="h-4 w-4 mr-2" /> Out for delivery
                    </Button>
                    <Button
                      onClick={() => openDeliverDialog(o)}
                      className="h-12 bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-lg font-semibold"
                      data-testid={`delivered-${o.id}`}
                    >
                      <KeyRound className="h-4 w-4 mr-2" /> Verify & Deliver
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {done.length > 0 && (
          <div>
            <h2 className="font-heading font-bold text-xl text-[#1C2826] mb-3">Completed</h2>
            <div className="space-y-2">
              {done.slice(0, 20).map((o) => (
                <div key={o.id} className="bg-white border border-[#E1E5DF] rounded-lg p-4 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-heading font-semibold">{o.number}</div>
                    <div className="text-xs text-[#738380]">{o.customer_name} · {new Date(o.created_at).toLocaleString("en-PH")}</div>
                  </div>
                  <div className="font-heading font-bold text-[#2F5233]">{peso(o.total)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delivery code verification dialog */}
      <Dialog open={!!verifyOrder} onOpenChange={(v) => !v && closeDeliverDialog()}>
        <DialogContent className="max-w-sm" data-testid="delivery-code-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#2F5233]" /> Delivery code</DialogTitle>
          </DialogHeader>
          {verifyOrder && (
            <div className="space-y-4">
              <div className="bg-[#F1F3EE] border border-[#E1E5DF] rounded-xl p-3 text-sm">
                <div className="text-xs text-[#738380]">Order</div>
                <div className="font-heading font-bold text-[#1C2826]">#{verifyOrder.number} · {verifyOrder.customer_name}</div>
              </div>
              <p className="text-sm text-[#4A5D5A] leading-snug">
                Ask the customer for the <span className="font-semibold text-[#1C2826]">4-digit code</span> shown in their My Orders screen. Enter it below to confirm delivery.
              </p>
              <Input
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                maxLength={4}
                placeholder="____"
                className="text-center text-3xl font-mono tracking-[0.6em] h-14"
                data-testid="delivery-code-input"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDeliverDialog} disabled={verifying}>Cancel</Button>
            <Button
              onClick={verifyAndDeliver}
              disabled={verifying || code.length !== 4}
              className="bg-[#2F5233] hover:bg-[#1F3D24] text-white"
              data-testid="delivery-code-submit"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> {verifying ? "Verifying…" : "Confirm delivery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
